import * as React from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Bot, X, Send, Sparkles, User, Plus } from 'lucide-react';
import { useSettings, type McpServerConfig } from '@/hooks/use-settings';
import { useAuth } from '@/hooks/use-auth';
import { getApiClient } from '@/lib/api-client';
import { VideoCarousel } from './video-carousel';
import { ImageGalleryModal } from './image-gallery-modal';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: {
    source?: 'mcp';
    serverId?: string;
    serverName?: string;
    status?: 'loading' | 'success' | 'error';
    toolName?: string;
    toolDescription?: string;
    videoData?: any; // Parsed video results for carousel
    pendingToolCalls?: Array<{
      name: string;
      args: Record<string, unknown>;
      toolUseId?: string;
    }>; // Tool calls waiting for confirmation
  };
}

interface AIChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const STORAGE_KEY_SESSION_ID = 'ai-chat-session-id';

export const AIChatPanel = React.memo(function AIChatPanel({ isOpen, onClose }: AIChatPanelProps) {
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [input, setInput] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [sessionId, setSessionId] = React.useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [beforeIndex, setBeforeIndex] = React.useState<number | undefined>(undefined);
  const [isMcpEnabled, setIsMcpEnabled] = React.useState(false);
  const [selectedMcpServers, setSelectedMcpServers] = React.useState<string[]>([]);
  const [expandedMcpMessages, setExpandedMcpMessages] = React.useState<Record<string, boolean>>({});
  const [pendingToolCalls, setPendingToolCalls] = React.useState<Array<{ name: string; args: Record<string, unknown>; toolUseId?: string }> | null>(null);
  const [imageGalleryOpen, setImageGalleryOpen] = React.useState(false);
  const [imageGalleryImages, setImageGalleryImages] = React.useState<any[]>([]);
  const [imageGalleryIndex, setImageGalleryIndex] = React.useState(0);
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  
  // Fetch settings to get model configuration
  const { data: settings } = useSettings();
  const { user } = useAuth();

  const availableMcpServers = React.useMemo<McpServerConfig[]>(() => {
    return (settings?.mcp_servers ?? [])
      .filter((server): server is McpServerConfig => {
        // Filter enabled servers (enabled defaults to true if not set)
        return server.enabled !== false;
      })
      .map((server, index) => ({
        ...server,
        // Generate id if not present (for backward compatibility)
        id: server.id || `mcp-${index}-${server.name}`,
      }));
  }, [settings]);

  const availableMcpServersMap = React.useMemo(() => {
    const map = new Map<string, (typeof availableMcpServers)[number]>();
    availableMcpServers.forEach((server) => {
      if (server.id) {
        map.set(server.id, server);
      }
    });
    return map;
  }, [availableMcpServers]);

  React.useEffect(() => {
    if (!isMcpEnabled) return;

    const availableIds = new Set(availableMcpServers.map((server) => server.id));
    setSelectedMcpServers((prev) => {
      const filtered = prev.filter((id) => availableIds.has(id));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [availableMcpServers, isMcpEnabled]);

  React.useEffect(() => {
    if (availableMcpServers.length === 0 && isMcpEnabled) {
      setIsMcpEnabled(false);
      setSelectedMcpServers([]);
    }
  }, [availableMcpServers, isMcpEnabled]);

  // Initialize session and load history when panel opens
  const isInitializingRef = React.useRef(false);
  const lastSessionIdRef = React.useRef<string | null>(null);
  
  React.useEffect(() => {
    if (!isOpen || !user) {
      // Reset initialization flag when panel closes
      isInitializingRef.current = false;
      return;
    }
    
    // Prevent duplicate initialization
    if (isInitializingRef.current) return;
    
    // Get session ID from localStorage first
    const currentSessionId = localStorage.getItem(STORAGE_KEY_SESSION_ID);
    
    // If we already have this session ID loaded, skip initialization
    if (currentSessionId && currentSessionId === lastSessionIdRef.current) {
      // Session already loaded, just set it if needed
      if (sessionId !== currentSessionId) {
        setSessionId(currentSessionId);
      }
      return;
    }
    
    isInitializingRef.current = true;
    lastSessionIdRef.current = currentSessionId;

    const initializeSession = async () => {
      const apiClient = getApiClient();
      
      // Get or create session ID
      let sessionIdToUse = localStorage.getItem(STORAGE_KEY_SESSION_ID);
      
      if (!sessionIdToUse) {
        // Create new session
        try {
          const response = await apiClient.request<{ sessionId: string }>('/chat/sessions/new', {
            method: 'POST',
          });
          sessionIdToUse = response.sessionId;
          localStorage.setItem(STORAGE_KEY_SESSION_ID, sessionIdToUse);
          lastSessionIdRef.current = sessionIdToUse;
        } catch (error) {
          console.error('Failed to create session:', error);
          isInitializingRef.current = false;
          return;
        }
      }
      
      setSessionId(sessionIdToUse);
      
      // Load all messages to restore full chat history
      try {
        const historyResponse = await apiClient.request<{
          messages: Array<{ 
            id: string; 
            role: 'user' | 'assistant'; 
            content: string; 
            timestamp: string;
            metadata?: any; // Include metadata in response type
          }>;
          hasMore: boolean;
          totalMessages: number;
        }>(`/chat/history/${sessionIdToUse}`);
        
        if (historyResponse.messages && historyResponse.messages.length > 0) {
          const loadedMessages: Message[] = historyResponse.messages.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.timestamp),
            metadata: normalizeMessageMetadata(msg.metadata),
          }));
          setMessages(loadedMessages);
          setHasMoreMessages(historyResponse.hasMore);
          // Set beforeIndex if there are more messages to load
          if (historyResponse.hasMore && historyResponse.totalMessages > loadedMessages.length) {
            // Calculate beforeIndex: totalMessages - loadedMessages.length
            setBeforeIndex(historyResponse.totalMessages - loadedMessages.length);
          } else {
            setBeforeIndex(undefined);
          }
        } else {
          // No history, show welcome message
          setMessages([{
            id: '1',
            role: 'assistant',
            content: 'Xin chào! Tôi là AI Assistant của TrangVang Admin. Tôi có thể giúp bạn viết nội dung, tạo mô tả, đề xuất SEO và nhiều hơn nữa. Bạn cần hỗ trợ gì?',
            timestamp: new Date(),
          }]);
          setHasMoreMessages(false);
          setBeforeIndex(undefined);
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
        // Show welcome message on error - don't block chat functionality
        setMessages([{
          id: '1',
          role: 'assistant',
          content: 'Xin chào! Tôi là AI Assistant của TrangVang Admin. Tôi có thể giúp bạn viết nội dung, tạo mô tả, đề xuất SEO và nhiều hơn nữa. Bạn cần hỗ trợ gì?',
          timestamp: new Date(),
        }]);
        setHasMoreMessages(false);
        setBeforeIndex(undefined);
      } finally {
        isInitializingRef.current = false;
      }
    };

    initializeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  // Auto scroll to bottom when new messages arrive
  React.useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  // Focus input when panel opens
  React.useEffect(() => {
    if (isOpen && inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  // Helper function to normalize videoData
  // ONLY processes valid JSON arrays - skips everything else silently
  const normalizeVideoData = React.useCallback((videoData: any): any[] | null => {
    if (!videoData) return null;
    
    // Quick check: if it's already a valid array, use it directly
    if (Array.isArray(videoData) && videoData.length > 0) {
      // Validate it's actually video data (has url or title)
      const isValidVideoArray = videoData.some((item: any) => 
        item && (typeof item === 'object') && (item.url || item.title)
      );
      if (isValidVideoArray) {
        return videoData.map((video: any) => ({
          url: video.url || '',
          title: video.title || 'Untitled',
          thumbnail: video.thumbnail || '',
          length: video.length || video.duration || 'Unknown',
          author: video.author,
          iframe_src: video.iframe_src,
          content: video.content,
        }));
      }
      return null;
    }
    
    // If it's a string, try to parse as JSON
    if (typeof videoData === 'string') {
      // Quick check: does it look like JSON? (starts with { or [)
      const trimmed = videoData.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        // Not JSON format, skip silently
        return null;
      }
      
      try {
        const parsed = JSON.parse(videoData);
        
        // Check if parsed result is an array with video data
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Validate it's actually video data
          const isValidVideoArray = parsed.some((item: any) => 
            item && (typeof item === 'object') && (item.url || item.title)
          );
          if (isValidVideoArray) {
            return parsed.map((video: any) => ({
              url: video.url || '',
              title: video.title || 'Untitled',
              thumbnail: video.thumbnail || '',
              length: video.length || video.duration || 'Unknown',
              author: video.author,
              iframe_src: video.iframe_src,
              content: video.content,
            }));
          }
        }
        
        // Check if parsed result is an object with videos array
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const videos = parsed.videos || parsed.results || parsed.data;
          if (Array.isArray(videos) && videos.length > 0) {
            const isValidVideoArray = videos.some((item: any) => 
              item && (typeof item === 'object') && (item.url || item.title)
            );
            if (isValidVideoArray) {
              return videos.map((video: any) => ({
                url: video.url || '',
                title: video.title || 'Untitled',
                thumbnail: video.thumbnail || '',
                length: video.length || video.duration || 'Unknown',
                author: video.author,
                iframe_src: video.iframe_src,
                content: video.content,
              }));
            }
          }
        }
      } catch (error) {
        // Not valid JSON or not video data format - skip silently
        // Don't log errors for history messages to avoid noise
        return null;
      }
    }
    
    // Not a valid format - skip silently
    return null;
  }, []);

  // Helper function to normalize imageData (similar to normalizeVideoData)
  const normalizeImageData = React.useCallback((imageData: any): any[] | null => {
    if (!imageData) return null;
    
    // Quick check: if it's already a valid array, use it directly
    if (Array.isArray(imageData) && imageData.length > 0) {
      // Validate it's actually image data (has url or thumbnail)
      const isValidImageArray = imageData.some((item: any) => 
        item && (typeof item === 'object') && (item.url || item.thumbnail || item.img_src || item.thumbnail_src)
      );
      if (isValidImageArray) {
        return imageData.map((img: any) => ({
          url: img.url || img.img_src || img.thumbnail_src || '',
          thumbnail: img.thumbnail || img.thumbnail_src || img.img_src || img.url || '',
          title: img.title || img.content || 'Untitled Image',
          source: img.url || img.img_src || '',
          width: img.width,
          height: img.height,
        }));
      }
      return null;
    }
    
    // If it's a string, try to parse as JSON
    if (typeof imageData === 'string') {
      const trimmed = imageData.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return null;
      }
      
      try {
        const parsed = JSON.parse(imageData);
        if (Array.isArray(parsed)) {
          const isValidImageArray = parsed.some((item: any) => 
            item && (typeof item === 'object') && (item.url || item.thumbnail || item.img_src)
          );
          if (isValidImageArray) {
            return parsed.map((img: any) => ({
              url: img.url || img.img_src || img.thumbnail_src || '',
              thumbnail: img.thumbnail || img.thumbnail_src || img.img_src || img.url || '',
              title: img.title || img.content || 'Untitled Image',
              source: img.url || img.img_src || '',
              width: img.width,
              height: img.height,
            }));
          }
        }
        
        // Check if parsed result is an object with images/results array
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const images = parsed.images || parsed.results || parsed.data;
          if (Array.isArray(images) && images.length > 0) {
            const isValidImageArray = images.some((item: any) => 
              item && (typeof item === 'object') && (item.url || item.thumbnail || item.img_src)
            );
            if (isValidImageArray) {
              return images.map((img: any) => ({
                url: img.url || img.img_src || img.thumbnail_src || '',
                thumbnail: img.thumbnail || img.thumbnail_src || img.img_src || img.url || '',
                title: img.title || img.content || 'Untitled Image',
                source: img.url || img.img_src || '',
                width: img.width,
                height: img.height,
              }));
            }
          }
        }
      } catch (error) {
        return null;
      }
    }
    
    return null;
  }, []);

  // Helper function to normalize message metadata
  // ONLY processes videoData and imageData if they're valid JSON - skips everything else silently
  const normalizeMessageMetadata = React.useCallback((metadata: any): any => {
    if (!metadata) return metadata;
    
    let normalizedMetadata = { ...metadata };
    
    // Process videoData if it exists
    if (metadata.videoData) {
      const normalized = normalizeVideoData(metadata.videoData);
      if (normalized && Array.isArray(normalized) && normalized.length > 0) {
        normalizedMetadata.videoData = normalized;
      } else {
        // Invalid or empty videoData - remove it silently
        const { videoData: _, ...rest } = normalizedMetadata;
        normalizedMetadata = rest;
      }
    }
    
    // Process imageData if it exists
    if (metadata.imageData) {
      const normalized = normalizeImageData(metadata.imageData);
      if (normalized && Array.isArray(normalized) && normalized.length > 0) {
        normalizedMetadata.imageData = normalized;
      } else {
        // Invalid or empty imageData - remove it silently
        const { imageData: _, ...rest } = normalizedMetadata;
        normalizedMetadata = rest;
      }
    }
    
    return normalizedMetadata;
  }, [normalizeVideoData, normalizeImageData]);

  // Save chat history after each message
  const saveHistory = React.useCallback(async (msgs: Message[]) => {
    if (!sessionId || !user) return;
    
    try {
      const apiClient = getApiClient();
      await apiClient.request('/chat/history/save', {
        method: 'POST',
        body: JSON.stringify({
          sessionId,
          messages: msgs.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp.toISOString(),
            metadata: msg.metadata, // Include metadata to preserve videoData and other metadata
          })),
        }),
      });
    } catch (error) {
      console.error('Failed to save chat history:', error);
      // Don't show error to user, just log it
      // History saving is not critical for chat functionality
    }
  }, [sessionId, user]);

  // Create new chat session
  const handleNewChat = React.useCallback(async () => {
    if (!user) return;
    
    try {
      const apiClient = getApiClient();
      const response = await apiClient.request<{ sessionId: string }>('/chat/sessions/new', {
        method: 'POST',
      });
      
      const newSessionId = response.sessionId;
      localStorage.setItem(STORAGE_KEY_SESSION_ID, newSessionId);
      setSessionId(newSessionId);
      setMessages([{
        id: '1',
        role: 'assistant',
        content: 'Xin chào! Tôi là AI Assistant của TrangVang Admin. Tôi có thể giúp bạn viết nội dung, tạo mô tả, đề xuất SEO và nhiều hơn nữa. Bạn cần hỗ trợ gì?',
        timestamp: new Date(),
      }]);
      setHasMoreMessages(false);
      setBeforeIndex(undefined);
    } catch (error) {
      console.error('Failed to create new session:', error);
    }
  }, [user]);

  // Load more messages
  const handleLoadMore = React.useCallback(async () => {
    if (!sessionId || !user || loadingMore || !hasMoreMessages) return;
    
    setLoadingMore(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.request<{
        messages: Array<{ 
          id: string; 
          role: 'user' | 'assistant'; 
          content: string; 
          timestamp: string;
          metadata?: any; // Include metadata
        }>;
        hasMore: boolean;
        nextBeforeIndex?: number;
      }>(`/chat/history/${sessionId}/more?limit=10${beforeIndex ? `&beforeIndex=${beforeIndex}` : ''}`);
      
      if (response.messages && response.messages.length > 0) {
        const loadedMessages: Message[] = response.messages.map((msg) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.timestamp),
          metadata: normalizeMessageMetadata(msg.metadata),
        }));
        
        setMessages((prev) => [...loadedMessages, ...prev]);
        setHasMoreMessages(response.hasMore);
        setBeforeIndex(response.nextBeforeIndex);
      }
    } catch (error) {
      console.error('Failed to load more messages:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [sessionId, user, loadingMore, hasMoreMessages, beforeIndex]);

  const handleToggleMcpMessage = React.useCallback((messageId: string) => {
    setExpandedMcpMessages((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  }, []);

  const handleSend = React.useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const currentInput = input.trim();
    setInput('');
    setIsLoading(true);

    const placeholderEntries: Array<{ id: string; serverId: string; serverName: string }> = [];
    let updatedMessages: Message[] = [];

    try {
      const apiClient = getApiClient();

      // QUICK POC: nếu đang bật MCP và câu hỏi có từ "video" hoặc "tìm", thử gọi trực
      // tiếp tool searxng_search_videos qua endpoint /chat/tools mà không
      // đi qua LLM, để tránh lỗi rate limit token của Claude.
      // Pattern matching: tìm video, video về, tìm kiếm video, search video, etc.
      const isVideoQuery =
        isMcpEnabled &&
        selectedMcpServers.length > 0 &&
        (/video/i.test(currentInput) || 
         /tìm.*video/i.test(currentInput) ||
         /search.*video/i.test(currentInput) ||
         /tìm kiếm.*video/i.test(currentInput));

      if (isVideoQuery) {
        console.log('[AIChatPanel] 🎬 Detected video query, using direct MCP tool call (bypassing LLM)');
        console.log('[AIChatPanel] Query:', currentInput);
        
        try {
          const toolResponse = await apiClient.request<{
            success?: boolean;
            output?: string;
            error?: string;
          }>('/chat/tools/searxng_search_videos', {
            method: 'POST',
            body: JSON.stringify({
              query: currentInput,
              language: 'vi',
              safesearch: 1,
              limit: 5,
            }),
          });
          
          console.log('[AIChatPanel] Direct tool response:', {
            success: toolResponse.success,
            hasOutput: !!toolResponse.output,
            hasError: !!toolResponse.error,
            outputLength: toolResponse.output?.length || 0,
          });

          if (!toolResponse || toolResponse.error || !toolResponse.output) {
            const errorMessage =
              toolResponse?.error ||
              'Không nhận được kết quả từ công cụ tìm kiếm video.';

            console.error('[AIChatPanel] Direct tool call failed:', errorMessage);

            const assistantMessage: Message = {
              id: (Date.now() + 1).toString(),
              role: 'assistant',
              content: `Xin lỗi, không thể tìm video vì: ${errorMessage}`,
              timestamp: new Date(),
            };

            setMessages((prev) => [...prev, assistantMessage]);
            await saveHistory([...messages, userMessage, assistantMessage]);
            setIsLoading(false);
            return;
          }

          // Parse RAW_JSON phần sau marker ---RAW_JSON---
          let videoDataFromMcp: any = null;
          let summaryText: string | null = null;
          try {
            const rawJsonMatch = toolResponse.output.match(/---RAW_JSON---\s*\n?([\s\S]*)$/);
            if (rawJsonMatch) {
              const rawJson = rawJsonMatch[1].trim();
              const parsed = JSON.parse(rawJson);
              const results = Array.isArray(parsed.results) ? parsed.results : [];
              const limited = results.slice(0, 5);

              videoDataFromMcp = limited.map((v: any) => ({
                url: v.url || '',
                title: v.title || '',
                thumbnail: v.thumbnail || v.img_src || null,
                author: v.author || v.publisher || null,
                length: v.length || v.duration || null,
                iframe_src: v.iframe_src || null,
              }));

              const titles = videoDataFromMcp
                .map((v: any) => v.title)
                .filter(Boolean)
                .slice(0, 3);
              summaryText =
                limited.length === 0
                  ? 'Không tìm thấy video nào phù hợp.'
                  : `Tôi đã tìm thấy ${limited.length} video. Một vài video tiêu biểu: ${titles.join(
                      ' | '
                    )}. Bạn có thể xem chi tiết trong danh sách video bên dưới.`;
              
              console.log('[AIChatPanel] ✅ Parsed video data:', {
                count: limited.length,
                titles: titles,
              });
            } else {
              console.warn('[AIChatPanel] ⚠️ No RAW_JSON marker found in tool response');
            }
          } catch (error) {
            console.error('[AIChatPanel] ❌ Failed to parse direct SearXNG result:', error);
          }

          const assistantMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content:
              summaryText ||
              'Tôi đã gọi công cụ tìm kiếm video và tìm thấy một số kết quả. Bạn có thể xem trong danh sách video bên dưới.',
            timestamp: new Date(),
            metadata: {
              videoData: Array.isArray(videoDataFromMcp) ? videoDataFromMcp : undefined,
            },
          };

          setMessages((prev) => [...prev, assistantMessage]);
          await saveHistory([...messages, userMessage, assistantMessage]);
          setIsLoading(false);
          return;
        } catch (error) {
          console.error('[AIChatPanel] ❌ Direct tool call error:', error);
          // Fall through to normal LLM flow if direct call fails
          console.log('[AIChatPanel] Falling back to LLM flow...');
        }
      }

      // === Flow mặc định: đi qua /chat (LLM + MCP) ===
      // Prepare messages for API
      // CRITICAL: Truncate history to avoid sending huge video results that trigger MCP calls
      // Only keep recent messages and truncate/filter content aggressively
      const MAX_HISTORY_MESSAGES = 5; // Limit history to last 5 messages (reduced from 10)
      const MAX_CONTENT_LENGTH = 200; // Truncate old message content to 200 chars (reduced from 500)
      const MAX_ERROR_MESSAGE_LENGTH = 100; // Error messages should be even shorter
      
      const recentMessages = messages.slice(-MAX_HISTORY_MESSAGES);
      const apiMessages = [
        ...recentMessages.map((msg) => {
          // Skip error messages entirely if they're too long
          const isError = msg.content.startsWith('Lỗi:') || msg.content.startsWith('Error:') || msg.content.includes('Failed to use');
          
          if (isError && msg.content.length > MAX_ERROR_MESSAGE_LENGTH) {
            // Replace long error messages with short summary
            return {
              role: msg.role,
              content: '[Đã xảy ra lỗi trước đó]',
            };
          }
          
          // If message has videoData in metadata, replace with short summary
          const hasVideoData = msg.metadata?.videoData && Array.isArray(msg.metadata.videoData) && msg.metadata.videoData.length > 0;
          
          let content = msg.content;
          
          // Truncate content if it's too long
          if (content.length > MAX_CONTENT_LENGTH) {
            // If it has video data, replace with short summary
            if (hasVideoData) {
              content = `[Đã tìm thấy ${msg.metadata.videoData.length} video trước đó]`;
            } else {
              // For other long content, truncate more aggressively
              // Try to keep the beginning if it's meaningful
              if (content.startsWith('Tìm thấy') || content.startsWith('Found')) {
                content = content.substring(0, MAX_CONTENT_LENGTH) + '...';
              } else {
                // For very long content, just use a placeholder
                content = '[Nội dung dài từ tin nhắn trước]';
              }
            }
          }
          
          return {
            role: msg.role,
            content: content,
          };
        }),
        {
          role: 'user' as const,
          content: currentInput,
        },
      ];
      
      // Calculate total size for logging
      const totalSize = JSON.stringify(apiMessages).length;
      console.log(`[AIChatPanel] 📝 Sending ${apiMessages.length} messages to API (limited from ${messages.length} total, size: ${totalSize} bytes)`);

      // Get model configuration from settings
      const modelSettings = settings?.model_settings;
      const chatModel = modelSettings?.chatModel;
      
      // Prepare options with model configuration
      const options: {
        modelId?: string;
        modelProvider?: string;
        modelApiKey?: string;
        modelBaseUrl?: string;
        systemPrompt?: string;
        mcpServerIds?: string[];
      } = {};
      
      // Use model from settings if available
      if (chatModel) {
        if (chatModel.modelName) {
          options.modelId = chatModel.modelName;
        }
        if (chatModel.provider) {
          options.modelProvider = chatModel.provider;
        }
        if (chatModel.apiKey) {
          options.modelApiKey = chatModel.apiKey;
        }
        if (chatModel.baseUrl) {
          options.modelBaseUrl = chatModel.baseUrl;
        }
        console.log('[AIChatPanel] Using model settings:', {
          provider: options.modelProvider,
          model: options.modelId,
          hasApiKey: !!options.modelApiKey,
          hasBaseUrl: !!options.modelBaseUrl,
        });
      } else {
        // Fallback: show warning if no model configured
        console.warn('No chat model configured in settings. Using default model.');
      }
      
      // Add system prompt for admin assistant
      options.systemPrompt = 'Bạn là AI Assistant của TrangVang Admin. Bạn giúp người dùng viết nội dung, tạo mô tả, đề xuất SEO và các tác vụ quản trị khác. Trả lời bằng tiếng Việt một cách chuyên nghiệp và hữu ích.';

      if (isMcpEnabled && selectedMcpServers.length > 0) {
        options.mcpServerIds = selectedMcpServers;
      }

      // NOTE: Trước đây chúng ta tạo các placeholder message cho từng MCP server
      // (example-server, mcp-searxng, mcp-code-executor). Điều này gây cảm giác
      // như đang gọi tất cả server song song và làm UI bị rối.
      // POC mới: không tạo placeholder riêng cho MCP nữa, chỉ hiển thị kết quả
      // cuối cùng (assistant message + carousel). Vì vậy block tạo placeholder
      // đã được loại bỏ.

      const response = await apiClient.request<{
        reply?: string;
        content?: string; // Gateway returns 'content' instead of 'reply'
        error?: string;
        model?: string;
        toolCalls?: Array<{
          name: string;
          args: Record<string, unknown>;
          toolUseId?: string;
        }>;
        mcpResults?: Array<{
          serverId: string;
          serverName: string;
          output?: string;
          error?: string;
          status: 'success' | 'error';
          toolName?: string;
          toolDescription?: string;
        }>;
      }>('/chat', {
        method: 'POST',
        body: JSON.stringify({
          messages: apiMessages,
          options,
        }),
      });

      if (response.error) {
        throw new Error(response.error);
      }

      // Gateway returns 'content', but API route might return 'reply' for backward compatibility
      const responseContent = response.content || response.reply;
      
      // Log response structure for debugging
      console.log(`[AIChatPanel] 📥 Received response:`, {
        hasReply: !!response.reply,
        hasContent: !!response.content,
        content: responseContent,
        hasToolCalls: !!response.toolCalls,
        toolCallsCount: response.toolCalls?.length || 0,
        hasMcpResults: !!response.mcpResults,
        mcpResultsCount: response.mcpResults?.length || 0,
        toolCallsNames: response.toolCalls?.map(tc => tc.name) || [],
        mcpResultsToolNames: response.mcpResults?.map(r => r.toolName) || [],
      });

      // Check if there are pending tool calls that need confirmation
      if (response.toolCalls && response.toolCalls.length > 0 && !response.mcpResults) {
        // Tool calls proposed but not executed - show confirmation
        setPendingToolCalls(response.toolCalls);
        
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: responseContent || 'Tôi muốn sử dụng công cụ để tìm kiếm thông tin. Bạn có đồng ý không?',
          timestamp: new Date(),
          metadata: {
            pendingToolCalls: response.toolCalls,
          },
        };
        
        setMessages((prev) => {
          const withoutPlaceholders =
            placeholderEntries.length > 0
              ? prev.filter(
                  (message) =>
                    !placeholderEntries.some((entry) => entry.id === message.id)
                )
              : prev;
          return [...withoutPlaceholders, assistantMessage];
        });
        
        setIsLoading(false);
        return;
      }

      // Parse video and image results from mcpResults to merge into LLM response.
      // Prefer normalized results from code executor (execute_javascript_on_data),
      // and fall back to raw SearXNG JSON if needed.
      let videoDataFromMcp: any = null;
      let videoSummaryFromMcp: string | null = null;
      let imageDataFromMcp: any = null;
      let imageSummaryFromMcp: string | null = null;
      const mcpResults = Array.isArray(response.mcpResults) ? response.mcpResults : [];
      
      console.log(`[AIChatPanel] Received ${mcpResults.length} MCP results`);
      
      if (mcpResults.length > 0) {
        console.log(`[AIChatPanel] MCP results:`, mcpResults.map(r => ({ 
          toolName: r.toolName, 
          status: r.status, 
          hasOutput: !!r.output,
          outputLength: r.output?.length || 0
        })));

        // 1) Try to use normalized result from code executor
        const normalizedResult = mcpResults.find(
          (r) => r.status === 'success' && r.toolName === 'execute_javascript_on_data' && r.output
        );

        if (normalizedResult?.output) {
          console.log(`[AIChatPanel] 🔍 Found execute_javascript_on_data result, output length: ${normalizedResult.output.length}`);
          console.log(`[AIChatPanel] Output preview (first 500 chars):`, normalizedResult.output.substring(0, 500));
          
          try {
            const parsed = JSON.parse(normalizedResult.output);
            console.log(`[AIChatPanel] Parsed JSON structure:`, {
              hasSuccess: 'success' in parsed,
              hasTask: 'task' in parsed,
              hasResult: 'result' in parsed,
              resultType: typeof parsed.result,
              resultIsObject: parsed.result && typeof parsed.result === 'object',
            });
            
            if (parsed && typeof parsed === 'object') {
              // The code executor returns { success, task, result }
              const resultPayload = 'result' in parsed ? (parsed as any).result : parsed;
              console.log(`[AIChatPanel] Result payload:`, {
                type: typeof resultPayload,
                isObject: resultPayload && typeof resultPayload === 'object',
                hasVideos: resultPayload && typeof resultPayload === 'object' && 'videos' in resultPayload,
                videosType: resultPayload && typeof resultPayload === 'object' ? typeof resultPayload.videos : 'N/A',
                videosIsArray: resultPayload && typeof resultPayload === 'object' ? Array.isArray(resultPayload.videos) : false,
                videosLength: resultPayload && typeof resultPayload === 'object' && Array.isArray(resultPayload.videos) ? resultPayload.videos.length : 0,
              });
              
              if (resultPayload && typeof resultPayload === 'object') {
                const payloadAny = resultPayload as any;

                if (Array.isArray(payloadAny.videos) && payloadAny.videos.length > 0) {
                  videoDataFromMcp = payloadAny.videos;
                  console.log(
                    `[VideoCarousel] ✅ Using normalized videos from code executor: ${payloadAny.videos.length} items`
                  );
                  console.log(`[VideoCarousel] First video:`, JSON.stringify(payloadAny.videos[0], null, 2));
                } else {
                  console.warn(`[AIChatPanel] ⚠️ Result payload has no videos array or it's empty:`, {
                    hasVideos: 'videos' in payloadAny,
                    videosValue: payloadAny.videos,
                    videosType: typeof payloadAny.videos,
                    isArray: Array.isArray(payloadAny.videos),
                  });
                }

                if (typeof payloadAny.summary === 'string' && payloadAny.summary.trim().length > 0) {
                  videoSummaryFromMcp = payloadAny.summary.trim();
                  console.log('[AIChatPanel] Video summary from code executor:', videoSummaryFromMcp);
                }
              } else {
                console.warn(`[AIChatPanel] ⚠️ Result payload is not an object:`, typeof resultPayload, resultPayload);
              }
            } else {
              console.warn(`[AIChatPanel] ⚠️ Parsed result is not an object:`, typeof parsed, parsed);
            }
          } catch (error) {
            console.error(
              '[AIChatPanel] ❌ Failed to parse normalized result from execute_javascript_on_data:',
              error
            );
            console.error('[AIChatPanel] Raw output that failed to parse:', normalizedResult.output.substring(0, 1000));
          }
        } else {
          console.log(`[AIChatPanel] No execute_javascript_on_data result found in ${mcpResults.length} MCP results`);
        }

        // 2) Parse from perplexica_search_videos if we still don't have video data
        if (!videoDataFromMcp) {
          // Try exact match first
          let perplexicaVideoResult = mcpResults.find(
            (r) => r.status === 'success' && r.toolName === 'perplexica_search_videos' && r.output
          );
          
          // If not found, try case-insensitive or partial match
          if (!perplexicaVideoResult) {
            perplexicaVideoResult = mcpResults.find(
              (r) => r.status === 'success' && 
              r.toolName && 
              r.toolName.toLowerCase().includes('perplexica') && 
              r.toolName.toLowerCase().includes('video') && 
              r.output
            );
          }
          
          // Log all tool names for debugging
          if (mcpResults.length > 0) {
            console.log(`[AIChatPanel] Available tool names:`, mcpResults.map(r => r.toolName));
          }
          
          if (perplexicaVideoResult && perplexicaVideoResult.output) {
            try {
              console.log(`[AIChatPanel] Found Perplexica video result with toolName: ${perplexicaVideoResult.toolName}`);
              console.log(`[AIChatPanel] Parsing Perplexica video output, length: ${perplexicaVideoResult.output.length}`);
              console.log(`[AIChatPanel] Output preview:`, perplexicaVideoResult.output.substring(0, 500));
              
              // Perplexica returns JSON directly: { videos: [...], videoCount: N }
              const parsed = JSON.parse(perplexicaVideoResult.output);
              console.log(`[AIChatPanel] Parsed JSON:`, JSON.stringify(parsed).substring(0, 500));
              
              if (parsed.videos && Array.isArray(parsed.videos) && parsed.videos.length > 0) {
                // Map Perplexica format to VideoCarousel format
                // Perplexica: { url, title, thumbnail, duration }
                // VideoCarousel: { url, title, thumbnail, length, author?, iframe_src?, content? }
                videoDataFromMcp = parsed.videos.map((video: any) => ({
                  url: video.url || '',
                  title: video.title || 'Untitled',
                  thumbnail: video.thumbnail || '',
                  length: video.duration || video.length || 'Unknown', // Map duration -> length
                  author: video.author,
                  iframe_src: video.iframe_src,
                  content: video.content,
                }));
                
                console.log(
                  `[VideoCarousel] ✅ Parsed ${videoDataFromMcp.length} videos from Perplexica`
                );
                console.log(`[VideoCarousel] First video:`, videoDataFromMcp[0]);
              } else {
                console.warn(`[AIChatPanel] Parsed JSON does not have videos array or is empty:`, parsed);
              }
            } catch (e) {
              console.error('❌ Failed to parse Perplexica video results:', e);
              console.error('❌ Output that failed to parse:', perplexicaVideoResult.output.substring(0, 1000));
            }
          } else {
            console.log(`[AIChatPanel] No Perplexica video result found. Available results:`, 
              mcpResults.filter(r => r.status === 'success').map(r => ({ toolName: r.toolName, hasOutput: !!r.output }))
            );
          }
        }

        // 3) Fallback: parse RAW_JSON from searxng_search_videos if we still don't have video data
        if (!videoDataFromMcp) {
          const videoResult = mcpResults.find(
            (r) => r.status === 'success' && r.toolName === 'searxng_search_videos' && r.output
          );
          
          console.log(`[AIChatPanel] Video result found (raw):`, !!videoResult);
          
          if (videoResult && videoResult.output) {
            try {
              console.log(`[AIChatPanel] Parsing raw video output, length: ${videoResult.output.length}`);
              
              const rawJsonMatch = videoResult.output.match(/---RAW_JSON---\s*\n?([\s\S]*)$/);
              if (rawJsonMatch) {
                const rawJson = rawJsonMatch[1].trim();
                const parsed = JSON.parse(rawJson);
                if (parsed.results && Array.isArray(parsed.results) && parsed.results.length > 0) {
                  videoDataFromMcp = parsed.results;
                  console.log(
                    `[VideoCarousel] ✅ Parsed ${parsed.results.length} videos from raw JSON (fallback)`
                  );
                }
              }
            } catch (e) {
              console.error('❌ Failed to parse fallback video results:', e);
            }
          }
        }

        // 4) Parse image results from searxng_search_images or perplexica_search_images
        const imageResult = mcpResults.find(
          (r) => r.status === 'success' && 
          (r.toolName === 'searxng_search_images' || r.toolName === 'perplexica_search_images') && 
          r.output
        );

        if (imageResult && imageResult.output) {
          console.log(`[AIChatPanel] 🖼️ Found image result with toolName: ${imageResult.toolName}`);
          console.log(`[AIChatPanel] Image output length: ${imageResult.output.length}`);
          
          try {
            // Try to parse as JSON first (Perplexica format)
            let parsed: any;
            try {
              parsed = JSON.parse(imageResult.output);
            } catch {
              // If not JSON, try to extract RAW_JSON from SearXNG format
              const rawJsonMatch = imageResult.output.match(/---RAW_JSON---\s*\n?([\s\S]*)$/);
              if (rawJsonMatch) {
                parsed = JSON.parse(rawJsonMatch[1].trim());
              } else {
                // Try parsing the whole output as JSON (might be direct JSON)
                parsed = JSON.parse(imageResult.output);
              }
            }

            // Extract images array from parsed result
            let images: any[] = [];
            if (parsed.images && Array.isArray(parsed.images)) {
              images = parsed.images;
            } else if (parsed.results && Array.isArray(parsed.results)) {
              images = parsed.results;
            }

            if (images.length > 0) {
              // Map to image gallery format: { url, thumbnail, title, source }
              imageDataFromMcp = images.slice(0, 20).map((img: any) => {
                // Get the best URL - prefer full image URL
                const fullUrl = img.url || img.img_src || img.image_url || img.src || '';
                const thumbUrl = img.thumbnail || img.thumbnail_src || img.thumb || img.thumbnail_url || '';
                
                // Use fullUrl as primary, fallback to thumbUrl if fullUrl is empty
                const primaryUrl = fullUrl || thumbUrl;
                const secondaryUrl = thumbUrl || fullUrl;
                
                return {
                  url: primaryUrl,
                  thumbnail: secondaryUrl,
                  title: img.title || img.content || img.caption || 'Untitled Image',
                  source: img.url || img.img_src || img.source || '',
                  width: img.width,
                  height: img.height,
                };
              });

              console.log(`[AIChatPanel] ✅ Parsed ${imageDataFromMcp.length} images`);
              if (imageDataFromMcp.length > 0) {
                console.log(`[AIChatPanel] First image sample:`, {
                  url: imageDataFromMcp[0].url,
                  thumbnail: imageDataFromMcp[0].thumbnail,
                  title: imageDataFromMcp[0].title,
                  hasUrl: !!imageDataFromMcp[0].url,
                  hasThumbnail: !!imageDataFromMcp[0].thumbnail,
                });
              }
              // Extract query from parsed result if available
              const query = parsed.query || '';
              imageSummaryFromMcp = query 
                ? `Tìm thấy ${images.length} hình ảnh về "${query}".`
                : `Tìm thấy ${images.length} hình ảnh.`;
            } else {
              console.warn(`[AIChatPanel] ⚠️ Parsed JSON does not have images/results array:`, parsed);
            }
          } catch (e) {
            console.error('❌ Failed to parse image results:', e);
            console.error('❌ Output that failed to parse:', imageResult.output.substring(0, 500));
          }
        }
      }

      // Clean up LLM response - extract content from <result></result> tags if present
      // LLM sometimes wraps tool results in <result> tags, we want to keep the content but remove the tags
      let cleanedContent = responseContent || 'Xin lỗi, không thể nhận được phản hồi từ AI.';
      
      // If content is wrapped in <result> tags, extract the inner content
      // Handle multiple <result> tags if present
      if (cleanedContent.includes('<result>')) {
        // Replace all <result> tags with their inner content
        cleanedContent = cleanedContent.replace(/<result>([\s\S]*?)<\/result>/gi, '$1');
        // Clean up any extra whitespace
        cleanedContent = cleanedContent.replace(/\n\s*\n\s*\n/g, '\n\n').trim();
      }
      
      // Nếu có dữ liệu video từ MCP, ưu tiên dùng summary ngắn thay vì để LLM
      // trả về toàn bộ danh sách markdown rất dài.
      let finalContent = cleanedContent;
      if (videoDataFromMcp && Array.isArray(videoDataFromMcp) && videoDataFromMcp.length > 0) {
        // Remove markdown video lists from LLM response if present
        // Pattern: ### Video 1:, **URL:**, **Title:**, etc.
        finalContent = cleanedContent
          .replace(/###\s*Video\s+\d+[:\s]*[\s\S]*?(?=###\s*Video\s+\d+|$)/gi, '')
          .replace(/\*\*Video\s+\d+[:\s]*\*\*[\s\S]*?(?=\*\*Video\s+\d+|$)/gi, '')
          .replace(/^\s*[-*]\s*Video\s+\d+[:\s]*[\s\S]*?(?=^\s*[-*]\s*Video\s+\d+|$)/gim, '')
          .trim();
        
        // If content is now empty or too short after removing video lists, use summary
        if (finalContent.length < 50 || finalContent.match(/^(Tìm thấy|Found|Đã tìm)/i)) {
          if (videoSummaryFromMcp && videoSummaryFromMcp.trim().length > 0) {
            finalContent = videoSummaryFromMcp;
          } else {
            const count = videoDataFromMcp.length;
            finalContent = `Tôi đã tìm thấy ${count} video phù hợp với yêu cầu của bạn. Bạn có thể xem và chọn trong danh sách video bên dưới.`;
          }
        }
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: finalContent,
        timestamp: new Date(),
        metadata: {
          videoData: videoDataFromMcp, // Merge video data into LLM response
          videoSummary: videoSummaryFromMcp || undefined,
          imageData: imageDataFromMcp, // Merge image data into LLM response
          imageSummary: imageSummaryFromMcp || undefined,
        },
      };
      
      console.log(`[AIChatPanel] ==========================================`);
      console.log(`[AIChatPanel] 📹 Assistant message created`);
      console.log(`[AIChatPanel] Has videoData:`, !!videoDataFromMcp);
      console.log(`[AIChatPanel] VideoData length:`, videoDataFromMcp?.length || 0);
      console.log(`[AIChatPanel] Has imageData:`, !!imageDataFromMcp);
      console.log(`[AIChatPanel] ImageData length:`, imageDataFromMcp?.length || 0);
      console.log(`[AIChatPanel] VideoData type:`, Array.isArray(videoDataFromMcp) ? 'array' : typeof videoDataFromMcp);
      console.log(`[AIChatPanel] ImageData type:`, Array.isArray(imageDataFromMcp) ? 'array' : typeof imageDataFromMcp);
      console.log(`[AIChatPanel] Is valid video array:`, Array.isArray(videoDataFromMcp) && videoDataFromMcp.length > 0);
      console.log(`[AIChatPanel] Is valid image array:`, Array.isArray(imageDataFromMcp) && imageDataFromMcp.length > 0);
      if (videoDataFromMcp && Array.isArray(videoDataFromMcp) && videoDataFromMcp.length > 0) {
        console.log(`[AIChatPanel] ✅ First video:`, JSON.stringify(videoDataFromMcp[0], null, 2));
        console.log(`[AIChatPanel] ✅ All videos:`, videoDataFromMcp.map(v => ({ url: v.url, title: v.title })));
      }
      if (imageDataFromMcp && Array.isArray(imageDataFromMcp) && imageDataFromMcp.length > 0) {
        console.log(`[AIChatPanel] ✅ First image:`, JSON.stringify(imageDataFromMcp[0], null, 2));
        console.log(`[AIChatPanel] ✅ All images:`, imageDataFromMcp.map(img => ({ url: img.url, thumbnail: img.thumbnail, title: img.title })));
      }
      if ((!videoDataFromMcp || !Array.isArray(videoDataFromMcp) || videoDataFromMcp.length === 0) && 
          (!imageDataFromMcp || !Array.isArray(imageDataFromMcp) || imageDataFromMcp.length === 0)) {
        console.warn(`[AIChatPanel] ⚠️ No video or image data found`);
        console.warn(`[AIChatPanel] ⚠️ videoDataFromMcp value:`, videoDataFromMcp);
        console.warn(`[AIChatPanel] ⚠️ imageDataFromMcp value:`, imageDataFromMcp);
        console.warn(`[AIChatPanel] ⚠️ MCP results summary:`, mcpResults.map(r => ({
          toolName: r.toolName,
          status: r.status,
          outputLength: r.output?.length || 0,
          outputPreview: r.output?.substring(0, 200) || 'no output'
        })));
      }
      console.log(`[AIChatPanel] Final content length:`, finalContent.length);
      console.log(`[AIChatPanel] Final content preview:`, finalContent.substring(0, 200));
      console.log(`[AIChatPanel] Full metadata:`, JSON.stringify(assistantMessage.metadata, null, 2));
      console.log(`[AIChatPanel] ==========================================`);

      setMessages((prev) => {
        const withoutPlaceholders =
          placeholderEntries.length > 0
            ? prev.filter(
                (message) =>
                  !placeholderEntries.some((entry) => entry.id === message.id)
              )
            : prev;

        // For now, we don't render separate MCP result bubbles in the UI.
        // We only use MCP results to enrich the main assistant message (e.g. video carousel).
        updatedMessages = [...withoutPlaceholders, assistantMessage];
        return updatedMessages;
      });

      // Save history after receiving response
      await saveHistory(updatedMessages);
    } catch (error) {
      console.error('Error calling chat API:', error);

      const genericErrorMessage =
        error instanceof Error
          ? error.message
          : 'Đã xảy ra lỗi khi kết nối với AI.';

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content:
          error instanceof Error
            ? `Lỗi: ${error.message}`
            : 'Xin lỗi, đã xảy ra lỗi khi kết nối với AI. Vui lòng thử lại sau.',
        timestamp: new Date(),
      };

      setMessages((prev) => {
        const withoutPlaceholders =
          placeholderEntries.length > 0
            ? prev.filter(
                (message) =>
                  !placeholderEntries.some((entry) => entry.id === message.id)
              )
            : prev;

        const mcpErrorMessages =
          placeholderEntries.length > 0
            ? placeholderEntries.map((entry) => ({
                id: `${entry.id}-error`,
                role: 'assistant' as const,
                content: `⚠️ MCP (${entry.serverName}) gặp lỗi: ${genericErrorMessage}`,
                timestamp: new Date(),
                metadata: {
                  source: 'mcp' as const,
                  status: 'error' as const,
                  serverId: entry.serverId,
                  serverName: entry.serverName,
                },
              }))
            : [];

        updatedMessages = [...withoutPlaceholders, ...mcpErrorMessages, errorMessage];
        return updatedMessages;
      });

      // Save history even on error
      await saveHistory(updatedMessages);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages, settings, saveHistory, isMcpEnabled, selectedMcpServers, availableMcpServersMap]);

  const handleKeyPress = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <div className="flex flex-col h-full w-full max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">AI Assistant</h3>
            <div className="flex items-center gap-2">
              <p className="text-xs text-gray-500">Trợ lý thông minh</p>
              {settings?.model_settings?.chatModel?.modelName && (
                <span className="text-xs text-gray-400 px-1.5 py-0.5 bg-gray-200 rounded">
                  {settings.model_settings.chatModel.provider === 'custom-openai' && 'Custom'}
                  {settings.model_settings.chatModel.provider === 'openai' && 'OpenAI'}
                  {settings.model_settings.chatModel.provider === 'anthropic' && 'Anthropic'}
                  {settings.model_settings.chatModel.provider === 'google' && 'Google'}
                  {settings.model_settings.chatModel.provider === 'cohere' && 'Cohere'}
                  {!['custom-openai', 'openai', 'anthropic', 'google', 'cohere'].includes(settings.model_settings.chatModel.provider || '') && settings.model_settings.chatModel.provider}
                  {': '}
                  {settings.model_settings.chatModel.modelName}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Test Video Carousel Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              const testMessage: Message = {
                id: `test-video-${Date.now()}`,
                role: 'assistant',
                content: 'Test video carousel with mock data',
                timestamp: new Date(),
                metadata: {
                  videoData: [
                    {
                      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
                      title: 'Test Video 1 - Sample YouTube Video',
                      thumbnail: 'https://i.ytimg.com/vi/dQw4w9WgXcQ/hqdefault.jpg',
                      author: 'Test Author',
                      length: '3:33',
                      iframe_src: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
                      content: 'This is a test video description'
                    },
                    {
                      url: 'https://www.youtube.com/watch?v=jNQXAC9IVRw',
                      title: 'Test Video 2 - Another Sample',
                      thumbnail: 'https://i.ytimg.com/vi/jNQXAC9IVRw/hqdefault.jpg',
                      author: 'Test Author 2',
                      length: '4:20',
                      iframe_src: 'https://www.youtube.com/embed/jNQXAC9IVRw',
                      content: 'Another test video'
                    }
                  ]
                }
              };
              setMessages((prev) => [...prev, testMessage]);
              console.log('[AIChatPanel] 🧪 Test message with video data added:', testMessage);
            }}
            className="h-8 w-8 text-gray-400 hover:text-gray-600"
            aria-label="Test video carousel"
            title="Test video carousel (DEV)"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNewChat}
            className="h-8 w-8 text-gray-400 hover:text-gray-600"
            aria-label="Tạo phiên chat mới"
            title="Tạo phiên chat mới"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 text-gray-400 hover:text-gray-600"
            aria-label="Đóng panel"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 overflow-x-hidden" ref={scrollAreaRef}>
        {/* Outer container to center content column and prevent horizontal overflow */}
        <div className="w-full max-w-full overflow-x-hidden px-3 py-4 flex justify-center">
          {/* Content column: all bubbles, MCP cards, carousel live inside this column */}
          <div className="space-y-4 w-full max-w-[360px]">
            {/* Load More Button */}
            {hasMoreMessages && (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="text-xs"
                >
                  {loadingMore ? 'Đang tải...' : 'Tải thêm tin nhắn cũ'}
                </Button>
              </div>
            )}
          {messages.map((message) => {
            // Tất cả messages (kể cả có metadata từ MCP) đều hiển thị theo kiểu
            // user/assistant bình thường. MCP chỉ dùng để enrich metadata (videoData,...).
            return (
              <div
                key={message.id}
                className={`flex flex-col gap-3 ${
                  message.role === 'user' ? 'items-end' : 'items-start'
                }`}
              >
                <div
                  className={`flex gap-3 w-full ${
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                  style={{ minWidth: 0, maxWidth: '100%' }}
                >
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                  )}
                  <div
                    className={`rounded-lg px-4 py-2 ${
                      message.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                    style={{
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      overflow: 'hidden',
                      minWidth: 0,
                      maxWidth: '100%',
                      flex: '0 1 auto',
                    }}
                  >
                    <p
                      className={`text-sm whitespace-pre-wrap break-words ${
                        message.role === 'user' ? 'text-white' : 'text-gray-900'
                      }`}
                      style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%',
                      }}
                    >
                      {message.content}
                    </p>
                    
                    {/* Tool call confirmation */}
                    {message.role === 'assistant' && message.metadata?.pendingToolCalls && message.metadata.pendingToolCalls.length > 0 && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs font-semibold text-yellow-900 mb-2">
                        Công cụ được đề xuất:
                      </p>
                      {message.metadata.pendingToolCalls.map((toolCall, idx) => (
                        <div key={idx} className="text-xs text-yellow-800 mb-1">
                          • {toolCall.name} {toolCall.args && Object.keys(toolCall.args).length > 0 && `(${JSON.stringify(toolCall.args)})`}
                        </div>
                      ))}
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={async () => {
                            // Execute tool calls - re-send with executeTools flag
                            setPendingToolCalls(null);
                            // Remove pending tool calls from message
                            setMessages((prev) =>
                              prev.map((msg) =>
                                msg.id === message.id
                                  ? {
                                      ...msg,
                                      metadata: {
                                        ...msg.metadata,
                                        pendingToolCalls: undefined,
                                      },
                                    }
                                  : msg
                              )
                            );
                            // Re-trigger send with executeTools=true
                            // This requires modifying the API call to include executeTools flag
                            // For now, we'll just remove the confirmation and let the next message trigger execution
                          }}
                          className="bg-primary text-white text-xs px-3 py-1 h-auto"
                        >
                          Đồng ý
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setPendingToolCalls(null);
                            setMessages((prev) =>
                              prev.map((msg) =>
                                msg.id === message.id
                                  ? {
                                      ...msg,
                                      metadata: {
                                        ...msg.metadata,
                                        pendingToolCalls: undefined,
                                      },
                                    }
                                  : msg
                              )
                            );
                          }}
                          className="text-xs px-3 py-1 h-auto"
                        >
                          Hủy
                        </Button>
                      </div>
                    </div>
                  )}
                    
                    <p
                      className={`text-xs mt-1 ${
                        message.role === 'user' ? 'text-white/80' : 'text-gray-500'
                      }`}
                    >
                      {message.timestamp.toLocaleTimeString('vi-VN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  {message.role === 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="w-4 h-4 text-gray-600" />
                    </div>
                  )}
                </div>
                
                {/* Debug: Show metadata to verify videoData is present */}
                {message.role === 'assistant' && message.metadata && (
                  <div className="w-full mt-2 max-w-full overflow-hidden">
                    <details className="text-xs bg-gray-100 rounded p-2">
                      <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-mono">
                        [DEBUG] Metadata (click to expand)
                      </summary>
                      <pre className="mt-2 text-xs overflow-auto max-h-40 bg-white p-2 rounded border break-all whitespace-pre-wrap" style={{ wordBreak: 'break-word' }}>
                        {JSON.stringify({
                          hasVideoData: !!message.metadata.videoData,
                          videoDataType: Array.isArray(message.metadata.videoData) ? 'array' : typeof message.metadata.videoData,
                          videoDataLength: Array.isArray(message.metadata.videoData) ? message.metadata.videoData.length : 'N/A',
                          metadata: message.metadata
                        }, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
                
                {/* Video Carousel in LLM response - render with proper width constraints */}
                {message.role === 'assistant' && message.metadata?.videoData && Array.isArray(message.metadata.videoData) && message.metadata.videoData.length > 0 ? (
                  <div className="w-full mt-4 max-w-full overflow-hidden">
                    <div className="mb-2 px-1 space-y-1">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        📹 Video Results ({message.metadata.videoData.length})
                      </p>
                      {message.metadata.videoSummary && (
                        <p className="text-xs text-gray-600 leading-snug">
                          {message.metadata.videoSummary}
                        </p>
                      )}
                    </div>
                    <div className="w-full max-w-full overflow-hidden">
                      <VideoCarousel videos={message.metadata.videoData} />
                    </div>
                  </div>
                ) : message.role === 'assistant' && message.metadata?.videoData ? (
                  <div className="w-full mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg max-w-full">
                    <p className="text-xs text-yellow-800">
                      ⚠️ Video data exists but failed validation check
                    </p>
                    <p className="text-xs text-yellow-600 mt-1 font-mono break-all">
                      Type: {typeof message.metadata.videoData}, 
                      IsArray: {String(Array.isArray(message.metadata.videoData))},
                      Length: {Array.isArray(message.metadata.videoData) ? message.metadata.videoData.length : 'N/A'}
                    </p>
                  </div>
                ) : null}

                {/* Image Gallery in LLM response */}
                {message.role === 'assistant' && message.metadata?.imageData && Array.isArray(message.metadata.imageData) && message.metadata.imageData.length > 0 ? (
                  <div className="w-full mt-4 max-w-full overflow-hidden">
                    <div className="mb-2 px-1 space-y-1">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        🖼️ Image Results ({message.metadata.imageData.length})
                      </p>
                      {message.metadata.imageSummary && (
                        <p className="text-xs text-gray-600 leading-snug">
                          {message.metadata.imageSummary}
                        </p>
                      )}
                    </div>
                    <div className="w-full max-w-full overflow-hidden">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                        {message.metadata.imageData.map((img: any, index: number) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => {
                              setImageGalleryImages(message.metadata.imageData);
                              setImageGalleryIndex(index);
                              setImageGalleryOpen(true);
                            }}
                            className="group relative aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200 hover:border-primary transition-all hover:shadow-md cursor-pointer"
                          >
                            <img
                              src={img.thumbnail || img.url || ''}
                              alt={img.title || `Image ${index + 1}`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              onError={(e) => {
                                // Fallback to a placeholder if image fails to load
                                const target = e.target as HTMLImageElement;
                                target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                              }}
                            />
                            {img.title && (
                              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <p className="text-xs truncate">{img.title}</p>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : message.role === 'assistant' && message.metadata?.imageData ? (
                  <div className="w-full mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg max-w-full">
                    <p className="text-xs text-yellow-800">
                      ⚠️ Image data exists but failed validation check
                    </p>
                    <p className="text-xs text-yellow-600 mt-1 font-mono break-all">
                      Type: {typeof message.metadata.imageData}, 
                      IsArray: {String(Array.isArray(message.metadata.imageData))},
                      Length: {Array.isArray(message.metadata.imageData) ? message.metadata.imageData.length : 'N/A'}
                    </p>
                  </div>
                ) : null}
              </div>
            );
          })}
            {isLoading && (
              <div className="flex gap-3 justify-start">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-primary" />
                </div>
                <div className="bg-gray-100 rounded-lg px-4 py-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-3">
        {availableMcpServers.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-gray-700">MCP Servers</p>
                <p className="text-xs text-gray-500">
                  Bật để gửi câu hỏi tới các máy chủ MCP và dùng kết quả vào câu trả lời.
                </p>
              </div>
              <Switch
                checked={isMcpEnabled}
                onCheckedChange={(checked) => {
                  setIsMcpEnabled(checked);
                  if (!checked) {
                    setSelectedMcpServers([]);
                  } else {
                    setSelectedMcpServers(
                      availableMcpServers.map((server) => server.id)
                    );
                  }
                }}
              />
            </div>
            {isMcpEnabled && (
              <div className="flex flex-wrap gap-2">
                {availableMcpServers.map((server) => {
                  const isActive = selectedMcpServers.includes(server.id);
                  return (
                    <button
                      key={server.id}
                      type="button"
                      onClick={() => {
                        setSelectedMcpServers((prev) =>
                          prev.includes(server.id)
                            ? prev.filter((id) => id !== server.id)
                            : [...prev, server.id]
                        );
                      }}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                        isActive
                          ? 'border-primary bg-primary text-white shadow-sm'
                          : 'border-primary/40 bg-white text-primary hover:border-primary hover:bg-primary/10'
                      }`}
                    >
                      {server.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Nhập câu hỏi hoặc yêu cầu..."
            disabled={isLoading}
            className="flex-1"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="px-4 bg-primary hover:bg-primary/90"
            aria-label="Gửi tin nhắn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Nhấn Enter để gửi • Shift+Enter để xuống dòng
        </p>
      </div>

      {/* Image Gallery Modal */}
      <ImageGalleryModal
        isOpen={imageGalleryOpen}
        images={imageGalleryImages}
        initialIndex={imageGalleryIndex}
        onClose={() => {
          setImageGalleryOpen(false);
          setImageGalleryImages([]);
          setImageGalleryIndex(0);
        }}
      />
    </div>
  );
});
